import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus, Plus, X, Award } from 'lucide-react';
import { createStaff, getSkills, type Skill, type SkillEvidence } from '@/services/staffService';
import { Badge } from '@/components/ui/badge';

interface AddStaffDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface SkillAssignment {
  skillId: string;
  skillName: string;
  proficiencyLevel: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  allocatedHours: number;
  evidence?: SkillEvidence;
}

interface Certificate {
  name: string;
  issuer: string;
  number?: string;
  expiryDate?: string;
}

export function AddStaffDialog({ isOpen, onClose, onSuccess }: AddStaffDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    email: '',
    department: '',
    seniority: 'Mid-level' as 'Junior' | 'Mid-level' | 'Senior' | 'Lead' | 'Principal',
    location: 'Office',
    status: 'active' as 'active' | 'inactive' | 'on-leave',
    totalCapacityHours: 40,
    availableCapacityHours: 40,
  });
  const [skills, setSkills] = useState<SkillAssignment[]>([]);
  const [certificates, setCertificates] = useState<Certificate[]>([]);

  // Load available skills when dialog opens
  useEffect(() => {
    if (isOpen) {
      loadSkills();
    }
  }, [isOpen]);

  const loadSkills = async () => {
    try {
      const response = await getSkills();
      if (response.success && response.data) {
        setAvailableSkills(response.data);
      }
    } catch (err) {
      console.error('Failed to load skills:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Calculate utilization percentage
      const utilizationPercent = formData.totalCapacityHours > 0
        ? Math.round(((formData.totalCapacityHours - formData.availableCapacityHours) / formData.totalCapacityHours) * 100)
        : 0;

      await createStaff({
        employeeId: `EMP${Date.now()}`,
        name: formData.name,
        email: formData.email,
        role: formData.role,
        department: formData.department,
        seniority: formData.seniority,
        status: formData.status,
        location: formData.location,
        totalCapacityHours: formData.totalCapacityHours,
        availableCapacityHours: formData.availableCapacityHours,
        utilizationPercent: utilizationPercent,
        skills: skills.map(s => ({
          skillId: s.skillId,
          skillName: s.skillName,
          proficiencyLevel: s.proficiencyLevel,
          allocatedHours: s.allocatedHours,
          ...(s.evidence && { evidence: s.evidence }),
        })),
        processes: [],
        customers: [],
      });
      
      // Reset form
      setFormData({
        name: '',
        role: '',
        email: '',
        department: '',
        seniority: 'Mid-level',
        location: 'Office',
        status: 'active',
        totalCapacityHours: 40,
        availableCapacityHours: 40,
      });
      setSkills([]);
      setCertificates([]);
      
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add staff member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addSkill = () => {
    if (availableSkills.length > 0) {
      const firstSkill = availableSkills[0];
      if (firstSkill) {
        setSkills([...skills, {
          skillId: firstSkill.id,
          skillName: firstSkill.name,
          proficiencyLevel: 'intermediate',
          allocatedHours: 5,
        }]);
      }
    }
  };

  const removeSkill = (index: number) => {
    setSkills(skills.filter((_, i) => i !== index));
  };

  const updateSkill = (index: number, field: keyof SkillAssignment, value: any) => {
    const updated = [...skills];
    const currentSkill = updated[index];
    if (currentSkill) {
      updated[index] = { ...currentSkill, [field]: value };
      setSkills(updated);
    }
  };

  const addCertificate = () => {
    setCertificates([...certificates, { name: '', issuer: '' }]);
  };

  const removeCertificate = (index: number) => {
    setCertificates(certificates.filter((_, i) => i !== index));
  };

  const updateCertificate = (index: number, field: keyof Certificate, value: string) => {
    const updated = [...certificates];
    const currentCert = updated[index];
    if (currentCert) {
      updated[index] = { ...currentCert, [field]: value };
      setCertificates(updated);
    }
  };

  const attachCertificateToSkill = (skillIndex: number, certIndex: number) => {
    const cert = certificates[certIndex];
    if (cert) {
      updateSkill(skillIndex, 'evidence', {
        certificateName: cert.name,
        certificateIssuer: cert.issuer,
        certificateNumber: cert.number,
        certificateExpiryDate: cert.expiryDate,
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add New Employee
          </DialogTitle>
          <DialogDescription>
            Add a new employee with skills, certifications, and capacity details.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 py-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-2 rounded-md text-sm">
                {error}
              </div>
            )}

            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-white border-b border-neutral-800 pb-2">Basic Information</h3>
              
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="John Doe"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="john.doe@line4.ai"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="role">Role *</Label>
                  <Input
                    id="role"
                    value={formData.role}
                    onChange={(e) => handleChange('role', e.target.value)}
                    placeholder="Compliance Officer"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="department">Department *</Label>
                  <Input
                    id="department"
                    value={formData.department}
                    onChange={(e) => handleChange('department', e.target.value)}
                    placeholder="Risk & Compliance"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="seniority">Seniority Level</Label>
                  <select
                    id="seniority"
                    value={formData.seniority}
                    onChange={(e) => handleChange('seniority', e.target.value)}
                    className="w-full px-3 py-2 bg-neutral-900 border border-neutral-700 rounded-md text-white"
                  >
                    <option value="Junior">Junior</option>
                    <option value="Mid-level">Mid-level</option>
                    <option value="Senior">Senior</option>
                    <option value="Lead">Lead</option>
                    <option value="Principal">Principal</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <select
                    id="location"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    className="w-full px-3 py-2 bg-neutral-900 border border-neutral-700 rounded-md text-white"
                  >
                    <option value="Office">Office</option>
                    <option value="Remote">Remote</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="Field">Field</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    className="w-full px-3 py-2 bg-neutral-900 border border-neutral-700 rounded-md text-white"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="on-leave">On Leave</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Capacity Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-white border-b border-neutral-800 pb-2">Capacity</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="totalCapacityHours">Total Capacity (hrs/week)</Label>
                  <Input
                    id="totalCapacityHours"
                    type="number"
                    min="1"
                    max="168"
                    value={formData.totalCapacityHours}
                    onChange={(e) => handleChange('totalCapacityHours', parseInt(e.target.value))}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="availableCapacityHours">Available Capacity (hrs/week)</Label>
                  <Input
                    id="availableCapacityHours"
                    type="number"
                    min="0"
                    max={formData.totalCapacityHours}
                    value={formData.availableCapacityHours}
                    onChange={(e) => handleChange('availableCapacityHours', parseInt(e.target.value))}
                  />
                </div>
              </div>
            </div>

            {/* Skills Assignment */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-neutral-800 pb-2">
                <h3 className="text-sm font-semibold text-white">Skills</h3>
                <Button
                  type="button"
                  onClick={addSkill}
                  size="sm"
                  variant="outline"
                  disabled={availableSkills.length === 0}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Skill
                </Button>
              </div>

              {skills.length === 0 ? (
                <p className="text-sm text-neutral-500 italic">No skills assigned yet</p>
              ) : (
                <div className="space-y-3">
                  {skills.map((skill, index) => (
                    <div key={index} className="p-3 bg-neutral-900 rounded-lg border border-neutral-800 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Skill</Label>
                            <select
                              value={skill.skillId}
                              onChange={(e) => {
                                const selected = availableSkills.find(s => s.id === e.target.value);
                                if (selected) {
                                  updateSkill(index, 'skillId', selected.id);
                                  updateSkill(index, 'skillName', selected.name);
                                }
                              }}
                              className="w-full px-2 py-1.5 bg-neutral-950 border border-neutral-700 rounded text-sm"
                            >
                              {availableSkills.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                              ))}
                            </select>
                          </div>

                          <div className="space-y-1">
                            <Label className="text-xs">Proficiency</Label>
                            <select
                              value={skill.proficiencyLevel}
                              onChange={(e) => updateSkill(index, 'proficiencyLevel', e.target.value)}
                              className="w-full px-2 py-1.5 bg-neutral-950 border border-neutral-700 rounded text-sm"
                            >
                              <option value="beginner">Beginner</option>
                              <option value="intermediate">Intermediate</option>
                              <option value="advanced">Advanced</option>
                              <option value="expert">Expert</option>
                            </select>
                          </div>
                        </div>

                        <Button
                          type="button"
                          onClick={() => removeSkill(index)}
                          size="sm"
                          variant="ghost"
                          className="ml-2"
                        >
                          <X className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs">Allocated Hours/Week</Label>
                        <Input
                          type="number"
                          min="0"
                          max={formData.availableCapacityHours}
                          value={skill.allocatedHours}
                          onChange={(e) => updateSkill(index, 'allocatedHours', parseInt(e.target.value))}
                          className="text-sm"
                        />
                      </div>

                      {skill.evidence && (
                        <div className="pt-2 border-t border-neutral-700">
                          <Badge variant="outline" className="text-xs">
                            <Award className="w-3 h-3 mr-1" />
                            {skill.evidence.certificateName}
                          </Badge>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Certifications */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-neutral-800 pb-2">
                <h3 className="text-sm font-semibold text-white">Certifications</h3>
                <Button
                  type="button"
                  onClick={addCertificate}
                  size="sm"
                  variant="outline"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Certificate
                </Button>
              </div>

              {certificates.length === 0 ? (
                <p className="text-sm text-neutral-500 italic">No certifications added yet</p>
              ) : (
                <div className="space-y-3">
                  {certificates.map((cert, index) => (
                    <div key={index} className="p-3 bg-neutral-900 rounded-lg border border-neutral-800 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Certificate Name *</Label>
                              <Input
                                value={cert.name}
                                onChange={(e) => updateCertificate(index, 'name', e.target.value)}
                                placeholder="AWS Solutions Architect"
                                className="text-sm"
                                required
                              />
                            </div>

                            <div className="space-y-1">
                              <Label className="text-xs">Issuer *</Label>
                              <Input
                                value={cert.issuer}
                                onChange={(e) => updateCertificate(index, 'issuer', e.target.value)}
                                placeholder="Amazon Web Services"
                                className="text-sm"
                                required
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Certificate Number</Label>
                              <Input
                                value={cert.number || ''}
                                onChange={(e) => updateCertificate(index, 'number', e.target.value)}
                                placeholder="CERT-12345"
                                className="text-sm"
                              />
                            </div>

                            <div className="space-y-1">
                              <Label className="text-xs">Expiry Date</Label>
                              <Input
                                type="date"
                                value={cert.expiryDate || ''}
                                onChange={(e) => updateCertificate(index, 'expiryDate', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                          </div>

                          {skills.length > 0 && (
                            <div className="space-y-1">
                              <Label className="text-xs">Link to Skill (Optional)</Label>
                              <select
                                onChange={(e) => {
                                  if (e.target.value) {
                                    attachCertificateToSkill(parseInt(e.target.value), index);
                                  }
                                }}
                                className="w-full px-2 py-1.5 bg-neutral-950 border border-neutral-700 rounded text-sm"
                              >
                                <option value="">-- Select a skill to link --</option>
                                {skills.map((s, i) => (
                                  <option key={i} value={i}>{s.skillName}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>

                        <Button
                          type="button"
                          onClick={() => removeCertificate(index)}
                          size="sm"
                          variant="ghost"
                        >
                          <X className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Adding...' : 'Add Employee'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
